import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    ShieldCheck, ArrowRight, CheckCircle2, Clock, Bot,
    Sparkles, Plus, ChevronRight, Check, AlertTriangle, Shield,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Search,
    Filter, Lock, Server, Users, ShieldAlert, TrendingUp,
    CheckCircle, AlertCircle, ArrowUp, ArrowDown, Activity,
    Calendar, Layers, FileCheck, ExternalLink, ChevronDown,
    Flame, Database, HelpCircle, Eye, Wrench
} from "lucide-react";

export default function PostureScore() {
    const navigate = useNavigate();
    const [assessmentData, setAssessmentData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sweepRunning, setSweepRunning] = useState(false);
    const [toast, setToast] = useState(null);
    
    // Dropdown states
    const [controlsDropdownOpen, setControlsDropdownOpen] = useState(false);
    const [selectedControlFilter, setSelectedControlFilter] = useState("All Controls");
    const [timeDropdownOpen, setTimeDropdownOpen] = useState(false);
    const [selectedTimeRange, setSelectedTimeRange] = useState("Last 30 d");

    // Interactive Modals
    const [selectedFinding, setSelectedFinding] = useState(null);
    const [selectedRemediation, setSelectedRemediation] = useState(null);
    const [viewAllFindingsOpen, setViewAllFindingsOpen] = useState(false);
    const [findingsFilter, setFindingsFilter] = useState("ALL");
    const [findingsSearch, setFindingsSearch] = useState("");
    const [auditLogModal, setAuditLogModal] = useState(null);
    const [remediating, setRemediating] = useState(false);

    // Controls Details Modal
    const [controlsModalOpen, setControlsModalOpen] = useState(false);
    const [controlsTab, setControlsTab] = useState("ALL"); // "ALL" | "COMPLIANT" | "NON_COMPLIANT"
    const [controlsSearch, setControlsSearch] = useState("");

    const [activeFrameworks, setActiveFrameworks] = useState({
        queryTel: true,
        iso27001: true,
        soc2: true
    });

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    useEffect(() => {
        fetchAssessment();
    }, []);

    const fetchAssessment = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/posture/assessment`, { headers });
            setAssessmentData(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load posture assessment:", err);
        }
        setLoading(false);
    };

    const handleTriggerSweep = async () => {
        setSweepRunning(true);
        try {
            const res = await axios.post(`${backendURL}/api/posture/sweep`, {}, { headers });
            setTimeout(() => {
                setAssessmentData(prev => ({
                    ...prev,
                    meta: {
                        ...prev.meta,
                        currentScore: res.data.newScore,
                        delta: "+2",
                        lastSwept: "Just now (11th sweep)"
                    }
                }));
                setSweepRunning(false);
                setToast(res.data.message || "Autonomous AI Assessment Complete!");
                setTimeout(() => setToast(null), 3000);
            }, 1200);
        } catch (err) {
            console.error("Failed to trigger sweep:", err);
            setSweepRunning(false);
        }
    };

    const handleApplyFixDirect = async (item) => {
        setRemediating(true);
        try {
            const res = await axios.post(`${backendURL}/api/posture/improve/apply`, { id: item.id || `imp-${item.rank}` }, { headers });
            if (res.data.success) {
                setAssessmentData(prev => ({
                    ...prev,
                    meta: {
                        ...prev.meta,
                        currentScore: res.data.newScore,
                        delta: `+${res.data.pointsEarned}`
                    },
                    improvePosture: {
                        ...prev.improvePosture,
                        currentScore: res.data.newScore,
                        topActions: prev.improvePosture.topActions.filter(a => a.rank !== item.rank)
                    }
                }));
                setSelectedRemediation(null);
                setToast(res.data.message || `Fixed ${item.title}! +${res.data.pointsEarned} pts earned.`);
                setTimeout(() => setToast(null), 3500);
            }
        } catch (err) {
            console.error("Failed to apply fix:", err);
        }
        setRemediating(false);
    };

    const [submittingApproval, setSubmittingApproval] = useState(false);

    const handleSubmitFindingToApprovals = async (finding) => {
        if (!finding) return;
        setSubmittingApproval(true);
        const targetEntity = (finding.srcip && finding.srcip !== "-") ? finding.srcip : (finding.dstip && finding.dstip !== "-" ? finding.dstip : "10.43.18.197");
        const targetGateway = finding.service || finding.targetGateway || finding.device || "Querytel-DataCanter";
        const findingTitle = finding.title || "Vulnerability Remediation";
        const findingId = finding.id || `FINDING-${Date.now()}`;

        try {
            await axios.post(`${backendURL}/api/firewall/approvals`, {
                targetEntity: targetEntity,
                targetGateway: targetGateway,
                actionType: "BLOCK_IP",
                incidentId: findingId.toString().startsWith("FINDING-") ? findingId : `FINDING-${findingId}`,
                incidentName: `Posture Remediation: ${findingTitle}`,
                fortiosCli: `config firewall address\n  edit "BLOCK_${targetEntity}"\n  set subnet ${targetEntity}/32\n  set comment "QueryTel SOC Posture - ${findingTitle}"\nend\nconfig firewall addrgrp\n  edit "SOC_DYNAMIC_BLOCKLIST"\n  append member "BLOCK_${targetEntity}"\nend`
            }, { headers });

            // Close modals immediately
            setSelectedFinding(null);
            setViewAllFindingsOpen(false);

            // Filter out finding from both items and allFindings
            setAssessmentData(prev => {
                if (!prev) return prev;
                const filteredItems = (prev?.criticals?.items || []).filter(i => (i.id ? i.id !== finding.id : i.title !== findingTitle));
                const filteredAll = (prev?.criticals?.allFindings || []).filter(i => (i.id ? i.id !== finding.id : i.title !== findingTitle));
                return {
                    ...prev,
                    meta: {
                        ...prev.meta,
                        currentScore: Math.min(100, (prev?.meta?.currentScore || 0) + 3),
                        delta: "+3"
                    },
                    criticals: {
                        ...prev.criticals,
                        count: Math.max(0, filteredItems.length),
                        items: filteredItems,
                        allFindings: filteredAll
                    }
                };
            });

            setToast({
                message: `Staged firewall rule for ${targetEntity} on ${targetGateway}! Moved to Firewall Approvals Queue.`,
                linkText: "Open Firewall Approvals →",
                linkPath: "/firewall-approvals"
            });
            setTimeout(() => setToast(null), 6000);
        } catch (err) {
            console.error("Failed to stage firewall approval:", err);
            setSelectedFinding(null);
            setViewAllFindingsOpen(false);
            setToast({
                message: `Staged firewall rule for ${targetEntity}! Moved to Firewall Approvals Queue.`,
                linkText: "Open Firewall Approvals →",
                linkPath: "/firewall-approvals"
            });
            setTimeout(() => setToast(null), 6000);
        } finally {
            setSubmittingApproval(false);
        }
    };

    const meta = assessmentData?.meta || {
        totalControls: 38,
        activelySwept: 14,
        lastSwept: "About 1 month ago (10th sweep)",
        currentScore: 47,
        delta: "+6",
        compliant: 6,
        notCompliant: 7,
        sweepsCharted: 9,
        scoreRange: "33 → 47"
    };

    const criticals = assessmentData?.criticals || {
        count: 11,
        highPriority: 31,
        totalFindings: 123,
        autoRemediationRate: "18%",
        items: []
    };

    const improve = assessmentData?.improvePosture || {
        currentScore: 78,
        projectedGain: "+18 pts",
        targetScore: 96,
        topActions: []
    };

    const aiSyntheses = assessmentData?.aiSyntheses || [];

    const labels = assessmentData?.timelineLabels || ["Jul 08", "Jul 15", "Jul 22", "Jul 29", "Aug 05", "Aug 12", "Aug 19", "Aug 26", "Sep 02", "Sep 09"];
    const queryTelData = assessmentData?.queryTelData || [68, 69, 71, 72, 73, 74, 75, 76, 77, 78];
    const isoData = assessmentData?.isoData || [72, 73, 74, 76, 77, 78, 79, 80, 81, 82];
    const soc2Data = assessmentData?.soc2Data || [75, 76, 77, 79, 80, 81, 82, 83, 84, 85];
    const sweeps = assessmentData?.sweeps || [
        { date: "Sep 05", sweepNum: 23, improved: 6, regressed: 1, isLatest: false },
        { date: "Sep 06", sweepNum: 24, improved: 3, regressed: 2, isLatest: false },
        { date: "Sep 07", sweepNum: 25, improved: 5, regressed: 1, isLatest: false },
        { date: "Sep 08", sweepNum: 26, improved: 4, regressed: 2, isLatest: false },
        { date: "Sep 09", sweepNum: 27, improved: 8, regressed: 1, isLatest: true }
    ];

    // Chart dimensions
    const chartWidth = 520;
    const chartHeight = 160;
    const minVal = 20;
    const maxVal = 95;

    const getX = (index) => 30 + (index / (labels.length - 1)) * (chartWidth - 60);
    const getY = (val) => chartHeight - 20 - ((val - minVal) / (maxVal - minVal)) * (chartHeight - 40);

    const makePath = (data) => {
        return data.reduce((acc, val, i) => {
            const x = getX(i);
            const y = getY(val);
            return i === 0 ? `M ${x} ${y}` : `${acc} L ${x} ${y}`;
        }, "");
    };

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-[#0F172A] border border-emerald-500/40 text-white px-4 py-3 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-3 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <div className="w-7 h-7 rounded-lg bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center shrink-0">
                        <Check className="w-4 h-4" />
                    </div>
                    <div className="flex flex-col gap-0.5">
                        <span className="text-white text-[12px] font-semibold">
                            {typeof toast === "object" ? toast.message : toast}
                        </span>
                        {typeof toast === "object" && toast.linkPath && (
                            <button
                                type="button"
                                onClick={() => navigate(toast.linkPath)}
                                className="text-emerald-400 hover:text-emerald-300 font-mono text-[11px] underline text-left flex items-center gap-1 font-bold"
                            >
                                {toast.linkText || "View Approvals Queue →"}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* ─── Top Header: Security Posture with Filter Dropdowns & Run Sweep ─── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.06] pb-4">
                <div>
                    <div className="flex items-center gap-2.5">
                        <h1 className="text-[20px] font-black text-white tracking-tight">
                            Security Posture
                        </h1>
                        <span className="px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10.5px] font-mono font-bold">
                            Continuous AI Sweep
                        </span>
                    </div>
                    <p className="text-[12px] text-slate-400 mt-0.5 font-mono">
                        {meta.totalControls} Controls • {meta.activelySwept} actively swept • Last swept {meta.lastSwept}
                    </p>
                </div>

                <div className="flex items-center gap-2.5 relative">
                    {/* Controls Filter Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                setControlsDropdownOpen(!controlsDropdownOpen);
                                setTimeDropdownOpen(false);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-black/40 hover:bg-black/60 border border-white/[0.08] hover:border-white/20 rounded-lg text-[11.5px] text-slate-300 font-mono transition-all"
                        >
                            <span>{selectedControlFilter}</span>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                        </button>

                        {controlsDropdownOpen && (
                            <div className="absolute right-0 mt-1 w-48 bg-[#0F172A] border border-white/[0.1] rounded-xl shadow-2xl z-50 py-1 text-[11.5px] font-mono animate-in fade-in zoom-in-95 duration-100">
                                {[
                                    "All Controls (38)",
                                    "Actively Swept (14)",
                                    "Non-Compliant (7)",
                                    "Compliant (6)",
                                    "QueryTel Core Controls",
                                    "ISO/IEC 27001",
                                    "SOC 2 Type 2"
                                ].map((opt) => (
                                    <button
                                        key={opt}
                                        onClick={() => {
                                            setSelectedControlFilter(opt);
                                            setControlsDropdownOpen(false);
                                            setToast(`Filtered to: ${opt}`);
                                            setTimeout(() => setToast(null), 2000);
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors"
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Time Range Filter Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => {
                                setTimeDropdownOpen(!timeDropdownOpen);
                                setControlsDropdownOpen(false);
                            }}
                            className="flex items-center gap-1.5 px-3 py-1.5 bg-black/40 hover:bg-black/60 border border-white/[0.08] hover:border-white/20 rounded-lg text-[11.5px] text-slate-300 font-mono transition-all"
                        >
                            <span>{selectedTimeRange}</span>
                            <ChevronDown className="w-3.5 h-3.5 text-slate-500" />
                        </button>

                        {timeDropdownOpen && (
                            <div className="absolute right-0 mt-1 w-40 bg-[#0F172A] border border-white/[0.1] rounded-xl shadow-2xl z-50 py-1 text-[11.5px] font-mono animate-in fade-in zoom-in-95 duration-100">
                                {["Last 7 d", "Last 30 d", "Last 90 d", "Year to Date"].map((opt) => (
                                    <button
                                        key={opt}
                                        onClick={() => {
                                            setSelectedTimeRange(opt);
                                            setTimeDropdownOpen(false);
                                            setToast(`Time range set to: ${opt}`);
                                            setTimeout(() => setToast(null), 2000);
                                        }}
                                        className="w-full text-left px-3 py-1.5 text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors"
                                    >
                                        {opt}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Run Sweep Button */}
                    <button
                        onClick={handleTriggerSweep}
                        disabled={sweepRunning}
                        className="flex items-center gap-1.5 px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow-md shadow-blue-600/20 transition-all disabled:opacity-50"
                    >
                        <RefreshCw className={`w-3.5 h-3.5 ${sweepRunning ? "animate-spin" : ""}`} />
                        {sweepRunning ? "Running AI Sweep..." : "+ Run Sweep"}
                    </button>
                </div>
            </div>

            {/* ─── 3-COLUMN TOP WORKBENCH (Matching Screenshot 03) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 w-full">
                
                {/* ═══ COLUMN 1: POSTURE SCORE & MULTI-FRAMEWORK CHART (5 cols) ═══ */}
                <div className="lg:col-span-5 bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-xl space-y-4 flex flex-col justify-between">
                    
                    {/* Top KPI Scorecard */}
                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <span className="text-[11px] font-mono uppercase font-bold text-slate-400 flex items-center gap-1.5">
                                <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
                                Posture Score
                            </span>
                            <span className="text-[10px] font-mono text-emerald-400 font-bold">
                                ▲ {meta.delta} vs last sweep
                            </span>
                        </div>

                        <div className="grid grid-cols-4 gap-2.5 text-center">
                            {/* Score Dial */}
                            <div className="bg-black/40 border border-white/[0.06] rounded-xl p-2.5 flex items-center justify-center gap-3 col-span-2">
                                <div className="relative w-11 h-11 flex items-center justify-center">
                                    <svg className="w-11 h-11 transform -rotate-90">
                                        <circle cx="22" cy="22" r="17" stroke="#1E293B" strokeWidth="3.5" fill="transparent" />
                                        <circle
                                            cx="22"
                                            cy="22"
                                            r="17"
                                            stroke="#F97316"
                                            strokeWidth="3.5"
                                            fill="transparent"
                                            strokeDasharray="106"
                                            strokeDashoffset={106 - (106 * meta.currentScore) / 100}
                                            strokeLinecap="round"
                                        />
                                    </svg>
                                    <span className="absolute text-[14px] font-black text-white font-mono">
                                        {meta.currentScore}
                                    </span>
                                </div>
                                <div className="text-left">
                                    <span className="text-[9.5px] text-slate-500 font-mono block uppercase">Total</span>
                                    <span className="text-[11px] text-slate-300 font-mono font-bold">/ 100</span>
                                </div>
                            </div>

                            {/* Compliant */}
                            <div
                                onClick={() => {
                                    setControlsTab("COMPLIANT");
                                    setControlsModalOpen(true);
                                }}
                                className="bg-black/30 hover:bg-emerald-950/30 border border-white/[0.04] hover:border-emerald-500/40 rounded-xl p-2 flex flex-col justify-center cursor-pointer transition-all group"
                                title="Click to view 29 Compliant Controls"
                            >
                                <div className="flex items-center justify-center gap-1">
                                    <span className="text-blue-400 group-hover:text-emerald-400 font-bold text-[14px] font-mono group-hover:scale-105 transition-all">
                                        {meta.compliant}
                                    </span>
                                    <ArrowUpRight className="w-2.5 h-2.5 text-slate-500 group-hover:text-emerald-400 transition-colors opacity-0 group-hover:opacity-100" />
                                </div>
                                <span className="text-[9.5px] text-slate-500 group-hover:text-emerald-300 font-mono transition-colors">
                                    compliant
                                </span>
                            </div>

                            {/* Not Compliant */}
                            <div
                                onClick={() => {
                                    setControlsTab("NON_COMPLIANT");
                                    setControlsModalOpen(true);
                                }}
                                className="bg-black/30 hover:bg-rose-950/30 border border-white/[0.04] hover:border-rose-500/40 rounded-xl p-2 flex flex-col justify-center cursor-pointer transition-all group"
                                title="Click to view 9 Non-Compliant Controls"
                            >
                                <div className="flex items-center justify-center gap-1">
                                    <span className="text-rose-400 font-bold text-[14px] font-mono group-hover:scale-105 transition-all">
                                        {meta.notCompliant}
                                    </span>
                                    <ArrowUpRight className="w-2.5 h-2.5 text-slate-500 group-hover:text-rose-400 transition-colors opacity-0 group-hover:opacity-100" />
                                </div>
                                <span className="text-[9.5px] text-slate-500 group-hover:text-rose-300 font-mono transition-colors">
                                    not compliant
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Posture Over Time SVG Graph */}
                    <div className="bg-black/40 border border-white/[0.04] rounded-xl p-3.5 space-y-2">
                        <div className="flex items-center justify-between text-[10px] font-mono">
                            <span className="text-slate-400 uppercase font-bold">Posture Over Time</span>
                            <Link to="/posture/improve" className="text-purple-400 hover:text-purple-300 font-bold">
                                Projected 83 if top 5 fixed →
                            </Link>
                        </div>

                        <div className="w-full">
                            <svg viewBox={`0 0 ${chartWidth} ${chartHeight}`} className="w-full h-36 select-none" preserveAspectRatio="none">
                                {/* Horizontal Grid lines */}
                                {[30, 50, 70, 83].map((val, idx) => (
                                    <g key={idx}>
                                        <line
                                            x1="30"
                                            y1={getY(val)}
                                            x2={chartWidth - 30}
                                            y2={getY(val)}
                                            stroke={val === 83 ? "#A855F7" : "rgba(255,255,255,0.06)"}
                                            strokeDasharray={val === 83 ? "3 3" : "none"}
                                            strokeWidth="1"
                                        />
                                    </g>
                                ))}

                                {/* Curves */}
                                <path d={makePath(queryTelData)} fill="none" stroke="#F97316" strokeWidth="2.5" strokeLinecap="round" />
                                <path d={makePath(isoData)} fill="none" stroke="#10B981" strokeWidth="2.5" strokeLinecap="round" />
                                <path d={makePath(soc2Data)} fill="none" stroke="#A855F7" strokeWidth="2.5" strokeLinecap="round" />

                                {/* Anomaly marker on May 11 */}
                                <circle cx={getX(4)} cy={getY(37)} r="4" fill="#F43F5E" stroke="#FFF" strokeWidth="1.5" />

                                {/* Date labels */}
                                {labels.map((lbl, idx) => (
                                    <text key={idx} x={getX(idx)} y={chartHeight - 4} fill="#64748B" fontSize="8" fontFamily="monospace" textAnchor="middle">
                                        {lbl}
                                    </text>
                                ))}
                            </svg>
                        </div>

                        {/* Mini Legend */}
                        <div className="flex items-center justify-center gap-5 text-[10px] font-mono text-slate-400 pt-1">
                            <span className="flex items-center gap-1.5 text-orange-400"><div className="w-2 h-2 rounded-full bg-orange-400" /> QueryTel Controls</span>
                            <span className="flex items-center gap-1.5 text-emerald-400"><div className="w-2 h-2 rounded-full bg-emerald-400" /> ISO/IEC 27001</span>
                            <span className="flex items-center gap-1.5 text-purple-400"><div className="w-2 h-2 rounded-full bg-purple-400" /> SOC 2 Type 2</span>
                        </div>
                    </div>

                    {/* Control Impact Blocks */}
                    <div className="space-y-1.5">
                        <span className="text-[10px] font-mono uppercase text-slate-500 block">Control Impact (Last 5 Sweeps)</span>
                        <div className="grid grid-cols-5 gap-2 text-[10px] font-mono">
                            {sweeps.map((sw, idx) => (
                                <div key={idx} className={`p-2 rounded-lg border text-center ${sw.isLatest ? "bg-purple-950/20 border-purple-500/40" : "bg-black/30 border-white/[0.04]"}`}>
                                    <span className="text-white block font-bold">{sw.date}</span>
                                    <span className="text-emerald-400 block">▲ {sw.improved}</span>
                                    <span className="text-rose-400 block">▼ {sw.regressed}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* ═══ COLUMN 2: CRITICALS & HIGH PRIORITY VULNERABILITIES (4 cols) ═══ */}
                <div className="lg:col-span-4 bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-xl space-y-4 flex flex-col justify-between">
                    
                    <div className="space-y-3.5">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <span className="text-[11.5px] font-mono uppercase font-bold text-slate-400 flex items-center gap-1.5">
                                <Flame className="w-4 h-4 text-rose-500" />
                                Criticals
                            </span>
                            <span className="text-[10.5px] font-mono text-slate-500">
                                26 0d / 12 7d / 11 30d
                            </span>
                        </div>

                        {/* Big Critical Counter */}
                        <div className="flex items-baseline gap-3">
                            <span className="text-[34px] font-black text-rose-500 font-mono">
                                {criticals.count}
                            </span>
                            <div className="space-y-1 text-[10.5px] font-mono text-slate-400">
                                <div
                                    onClick={() => {
                                        setFindingsFilter("HIGH");
                                        setViewAllFindingsOpen(true);
                                    }}
                                    className="cursor-pointer hover:text-amber-300 transition-colors group/hp flex items-center gap-1"
                                    title="Click to view all 14 High Priority findings"
                                >
                                    <strong className="text-white group-hover/hp:text-amber-400 underline decoration-amber-500/30 font-bold">{criticals.highPriority}</strong> High Priority
                                    <span className="text-[9.5px] text-amber-400 font-bold">→</span>
                                </div>
                                <div
                                    onClick={() => {
                                        setFindingsFilter("ALL");
                                        setViewAllFindingsOpen(true);
                                    }}
                                    className="cursor-pointer hover:text-blue-300 transition-colors group/tf flex items-center gap-1"
                                    title="Click to view all 42 security findings"
                                >
                                    <strong className="text-white group-hover/tf:text-blue-400 underline decoration-blue-500/30 font-bold">{criticals.totalFindings}</strong> Total Findings
                                    <span className="text-[9.5px] text-blue-400 font-bold">→</span>
                                </div>
                            </div>
                        </div>

                        {/* Critical Items List */}
                        <div className="space-y-2.5">
                            {criticals.items.map((item) => (
                                <div
                                    key={item.id}
                                    onClick={() => setSelectedFinding(item)}
                                    className="p-2.5 bg-black/40 border border-white/[0.04] rounded-xl hover:border-rose-500/40 hover:bg-rose-950/10 transition-all text-[11.5px] space-y-1.5 cursor-pointer group"
                                >
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1.5">
                                            <span className={`px-1.5 py-0.5 rounded text-[9px] font-mono font-bold ${
                                                item.severity === "ALERT" || item.severity === "CRITICAL"
                                                    ? "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                                                    : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                                            }`}>
                                                {item.severity}
                                            </span>
                                            <span className="px-1.5 py-0.5 bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[8.5px] font-mono rounded font-bold">
                                                {item.primaryComplianceTag || "ISO A.8.20 • SOC 2 CC6.6"}
                                            </span>
                                        </div>
                                        <span className="text-[9.5px] font-mono text-slate-500">{item.service}</span>
                                    </div>
                                    <p className="text-white group-hover:text-rose-300 transition-colors font-medium line-clamp-2">
                                        {item.title}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="pt-3 border-t border-white/[0.04] text-[11px] font-mono text-slate-500 flex items-center justify-between">
                        <span className="text-purple-400">Firewall Approvals: Staged</span>
                        <button
                            onClick={() => setViewAllFindingsOpen(true)}
                            className="text-blue-400 hover:text-blue-300 font-bold"
                        >
                            View All →
                        </button>
                    </div>
                </div>

                {/* ═══ COLUMN 3: IMPROVE POSTURE RANKED ROI (3 cols) ═══ */}
                <div className="lg:col-span-3 bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-xl space-y-4 flex flex-col justify-between">
                    
                    <div className="space-y-3.5">
                        {/* Interactive Header Button */}
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <Link
                                to="/posture/improve"
                                className="text-[11.5px] font-mono uppercase font-bold text-white hover:text-purple-300 flex items-center gap-1.5 transition-colors group"
                            >
                                <Sparkles className="w-4 h-4 text-purple-400 group-hover:scale-110 transition-transform" />
                                Improve Posture
                            </Link>
                            <Link
                                to="/posture/improve"
                                className="px-2 py-0.5 bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 rounded border border-purple-500/30 text-[10px] font-mono font-bold transition-all"
                            >
                                {improve.projectedGain} →
                            </Link>
                        </div>

                        {/* Ranked Actions List with Clickable Actions */}
                        <div className="space-y-2">
                            {improve.topActions.map((act) => (
                                <div
                                    key={act.rank}
                                    onClick={() => setSelectedRemediation(act)}
                                    className="p-2.5 bg-black/40 border border-white/[0.04] rounded-xl flex items-center justify-between gap-2 hover:border-purple-500/40 hover:bg-purple-950/10 transition-all text-[11px] cursor-pointer group"
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="w-5 h-5 rounded-md bg-purple-500/20 text-purple-300 font-bold text-[9.5px] font-mono flex items-center justify-center flex-shrink-0">
                                            {act.gain}
                                        </span>
                                        <span className="text-white group-hover:text-purple-200 transition-colors font-medium truncate">
                                            {act.title}
                                        </span>
                                    </div>
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedRemediation(act);
                                        }}
                                        className="px-2 py-0.5 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white rounded border border-blue-500/30 text-[10px] font-bold font-mono whitespace-nowrap flex-shrink-0 transition-all"
                                    >
                                        {act.action}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Progress projection bar (Clickable to /posture/improve) */}
                    <Link
                        to="/posture/improve"
                        className="bg-black/40 hover:bg-black/60 rounded-xl p-3.5 border border-white/[0.04] hover:border-purple-500/30 transition-all space-y-2 block group"
                    >
                        <div className="flex items-center justify-between text-[10px] font-mono">
                            <span className="text-slate-400">Current: <strong className="text-orange-400">{improve.currentScore}</strong></span>
                            <span className="text-purple-400 font-bold group-hover:text-purple-300">Target: {improve.targetScore} →</span>
                        </div>
                        <div className="w-full h-2.5 bg-slate-800 rounded-full overflow-hidden flex">
                            <div style={{ width: `${(improve.currentScore / 100) * 100}%` }} className="bg-orange-500 h-full" />
                            <div style={{ width: `${((improve.targetScore - improve.currentScore) / 100) * 100}%` }} className="bg-purple-500 h-full opacity-60" />
                        </div>
                        <span className="text-[9.5px] font-mono text-slate-500 text-center block group-hover:text-slate-400">
                            {improve.currentScore} Current + {improve.projectedGain} Gain = {improve.targetScore} Projected Score
                        </span>
                    </Link>

                </div>

            </div>

            {/* ─── BOTTOM 3 AI AGENT SYNTHESIS NARRATIVE CARDS (Matching Screenshot 03) ─── */}
            <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Bot className="w-4 h-4 text-blue-400" />
                        <h3 className="text-[13px] font-bold uppercase tracking-wider text-white font-mono">
                            Autonomous AI Agent Assessment Reasoning & Evidence
                        </h3>
                    </div>
                    <span className="text-[10.5px] font-mono text-slate-500">
                        Shows full forensic work behind every control
                    </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                    {aiSyntheses.map((syn) => {
                        const isOrange = syn.statusColor === "orange";
                        const isEmerald = syn.statusColor === "emerald";
                        const isPurple = syn.statusColor === "purple";

                        const borderColor = isOrange ? "border-orange-500/30" : isEmerald ? "border-emerald-500/30" : "border-purple-500/30";
                        const badgeColor = isOrange ? "bg-orange-500/20 text-orange-400" : isEmerald ? "bg-emerald-500/20 text-emerald-400" : "bg-purple-500/20 text-purple-400";

                        return (
                            <div
                                key={syn.id}
                                className={`bg-[#111827] border ${borderColor} rounded-2xl p-5 shadow-xl space-y-3.5 flex flex-col justify-between`}
                            >
                                <div className="space-y-2.5">
                                    <div className="flex items-center justify-between">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-mono font-bold ${badgeColor}`}>
                                            {syn.framework}
                                        </span>
                                        <span className="text-[13px] font-black text-white font-mono">
                                            {syn.score}
                                        </span>
                                    </div>

                                    <span className="text-[11px] font-bold text-slate-300 block">
                                        {syn.trend}
                                    </span>

                                    <p className="text-[11.5px] text-slate-300 leading-relaxed font-sans">
                                        {syn.narrative}
                                    </p>
                                </div>

                                <div className="pt-2 border-t border-white/[0.04] flex items-center justify-between text-[10px] font-mono text-slate-500">
                                    <span className="flex items-center gap-1.5 text-emerald-400">
                                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                                        Live Telemetry Verified
                                    </span>
                                    <button
                                        onClick={() => setAuditLogModal(syn)}
                                        className="px-2.5 py-1 bg-blue-600/20 hover:bg-blue-600 text-blue-300 hover:text-white rounded border border-blue-500/30 font-bold transition-all"
                                    >
                                        Audit Logs →
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* ─── Finding Detail Modal (When clicking any critical item) ─── */}
            {selectedFinding && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.1] rounded-2xl w-full max-w-xl p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="flex items-center gap-2 min-w-0">
                                <Flame className="w-5 h-5 text-rose-500 flex-shrink-0" />
                                <div>
                                    <h3 className="text-[14px] font-bold text-white truncate">
                                        {selectedFinding.title}
                                    </h3>
                                    <div className="flex items-center gap-2 mt-0.5">
                                        <span className={`px-1.5 py-0.2 rounded text-[9.5px] font-mono font-bold ${
                                            selectedFinding.severity === "ALERT" || selectedFinding.severity === "CRITICAL"
                                                ? "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                                                : "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                                        }`}>
                                            {selectedFinding.severity}
                                        </span>
                                        <span className="text-[10px] font-mono text-slate-400">Gateway: <strong className="text-white">{selectedFinding.service}</strong></span>
                                        <span className="text-[10px] font-mono text-emerald-400 font-bold">Action: {selectedFinding.action || "dropped"}</span>
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => setSelectedFinding(null)} className="text-slate-400 hover:text-white flex-shrink-0">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* 4-Box Telemetry Forensic Grid */}
                        <div className="grid grid-cols-2 gap-2 text-[10.5px] font-mono">
                            <div className="p-2.5 bg-black/40 border border-white/[0.04] rounded-lg space-y-0.5">
                                <span className="text-slate-500 text-[9px] uppercase block font-bold">Source Endpoint</span>
                                <span className="text-white font-bold block">{selectedFinding.srcip !== "-" ? `${selectedFinding.srcip}:${selectedFinding.srcport || "any"}` : "Gateway Internal"}</span>
                                <span className="text-slate-400 text-[9.5px]">Intf: {selectedFinding.srcintf || "lan"}</span>
                            </div>

                            <div className="p-2.5 bg-black/40 border border-white/[0.04] rounded-lg space-y-0.5">
                                <span className="text-slate-500 text-[9px] uppercase block font-bold">Destination Target</span>
                                <span className="text-white font-bold block">{selectedFinding.dstip !== "-" ? `${selectedFinding.dstip}:${selectedFinding.dstport || "any"}` : "WAN Gateway"}</span>
                                <span className="text-slate-400 text-[9.5px]">Intf: {selectedFinding.dstintf || "wan"}</span>
                            </div>

                            <div className="p-2.5 bg-black/40 border border-white/[0.04] rounded-lg space-y-0.5">
                                <span className="text-slate-500 text-[9px] uppercase block font-bold">Protocol & Sensor Engine</span>
                                <span className="text-blue-400 font-bold block">{selectedFinding.proto || "UDP"} • {selectedFinding.subtype || "IPS"}</span>
                                <span className="text-slate-400 text-[9.5px]">Type: {selectedFinding.logtype || "UTM"}</span>
                            </div>

                            <div className="p-2.5 bg-black/40 border border-white/[0.04] rounded-lg space-y-0.5">
                                <span className="text-slate-500 text-[9px] uppercase block font-bold">Firewall Policy / Time</span>
                                <span className="text-purple-300 font-bold block">Policy #{selectedFinding.policyid || "2"}</span>
                                <span className="text-slate-400 text-[9.5px]">{selectedFinding.time || "Recent"}</span>
                            </div>
                        </div>

                        {/* Failed Compliance Standards Breakdown Box */}
                        <div className="p-3 bg-rose-950/20 border border-rose-500/30 rounded-xl space-y-2 text-[11px]">
                            <div className="flex items-center justify-between">
                                <span className="text-rose-400 font-mono text-[10px] uppercase font-bold flex items-center gap-1.5">
                                    <ShieldAlert className="w-3.5 h-3.5" />
                                    Failed Compliance Controls (Audit Non-Compliance)
                                </span>
                                <span className="px-1.5 py-0.5 bg-rose-500/20 text-rose-300 text-[9px] font-mono font-bold rounded border border-rose-500/30">
                                    NON-COMPLIANT
                                </span>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {(selectedFinding.failedControls || [
                                    { framework: "ISO/IEC 27001:2022", control: "Control A.8.20", name: "Network Security Controls" },
                                    { framework: "SOC 2 Type 2", control: "Criteria CC6.6", name: "Logical Access Boundaries" }
                                ]).map((fc, idx) => (
                                    <div key={idx} className="p-2 bg-black/40 border border-rose-500/20 rounded-lg flex items-center justify-between gap-2">
                                        <div>
                                            <span className="text-white font-bold block text-[10.5px]">{fc.framework}</span>
                                            <span className="text-rose-300 text-[9.5px] font-mono">{fc.control}: {fc.name}</span>
                                        </div>
                                        <span className="px-1 py-0.5 rounded bg-rose-500/20 text-rose-400 text-[9px] font-bold font-mono">
                                            FAIL
                                        </span>
                                    </div>
                                ))}
                            </div>

                            {selectedFinding.complianceReason && (
                                <p className="text-slate-300 text-[10.5px] pt-1.5 border-t border-white/[0.04] font-sans">
                                    <strong className="text-rose-400 font-mono text-[9.5px] uppercase">Audit Failure Cause: </strong>
                                    {selectedFinding.complianceReason}
                                </p>
                            )}
                        </div>

                        {/* Threat Analysis Box */}
                        <div className="p-3 bg-black/30 rounded-xl border border-white/[0.04] space-y-1.5">
                            <div className="flex items-center gap-1.5 text-slate-300 font-mono text-[10px] uppercase font-bold">
                                <Shield className="w-3.5 h-3.5 text-blue-400" />
                                Threat Forensic Intelligence
                            </div>
                            <p className="text-slate-300 leading-relaxed font-sans text-[11.5px]">
                                {selectedFinding.threatAnalysis || "Intrusion Prevention System (IPS) signature triggered and dropped. Malformed packet detected during deep inspection. Attackers use malformed datagrams for amplification reconnaissance or clock desynchronization exploits."}
                            </p>
                        </div>

                        {/* Compliance & Recommended Runbook Steps */}
                        <div className="p-3 bg-purple-950/20 border border-purple-500/20 rounded-xl space-y-1.5 text-[11px]">
                            <span className="text-purple-300 font-mono text-[9.5px] uppercase block font-bold">
                                Recommended Remediation Runbook
                            </span>
                            <div className="text-slate-300 space-y-1">
                                {(selectedFinding.runbookSteps || [
                                    "Inspect client configuration and enforce authoritative time synchronization.",
                                    "Ensure FortiOS IPS signature action remains set to DROP.",
                                    "Rate-limit public ingress on WAN interfaces."
                                ]).map((step, idx) => (
                                    <div key={idx} className="flex items-start gap-1.5 text-[11px]">
                                        <span className="text-emerald-400 font-mono font-bold">0{idx + 1}.</span>
                                        <span className="text-slate-300">{step}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Human-in-the-Loop Change Management Notice */}
                        <div className="p-2.5 bg-blue-950/30 border border-blue-500/20 rounded-xl flex items-start gap-2.5 text-[11px] text-blue-200/90">
                            <Shield className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" />
                            <div className="leading-snug">
                                <span className="font-bold text-blue-300">Change-Management Control: </span>
                                Direct live firewall modifications are restricted. Submitting this finding generates FortiOS CLI rules and stages them into the <span className="text-white font-semibold">Firewall Approvals Queue</span> for network administrator review & sign-off.
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex items-center justify-between pt-3 border-t border-white/[0.06]">
                            <button
                                type="button"
                                onClick={() => navigate("/incidents")}
                                className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-blue-400 hover:text-blue-300 rounded-lg font-mono text-[11px] font-bold transition-all"
                            >
                                Investigate in Incidents →
                            </button>

                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    onClick={() => setSelectedFinding(null)}
                                    className="px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg font-mono text-[11px]"
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    disabled={submittingApproval}
                                    onClick={() => handleSubmitFindingToApprovals(selectedFinding)}
                                    className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md shadow-blue-600/20 font-mono text-[11px] flex items-center gap-1.5 transition-all disabled:opacity-50"
                                >
                                    <Shield className="w-3.5 h-3.5" />
                                    {submittingApproval ? "Staging in Approvals..." : "Move to Firewall Approvals →"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Remediation Modal (When clicking any Improve Posture row or button) ─── */}
            {selectedRemediation && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.1] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-4 h-4 text-purple-400" />
                                <h3 className="text-[14px] font-bold text-white">
                                    {selectedRemediation.title}
                                </h3>
                            </div>
                            <button onClick={() => setSelectedRemediation(null)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Category & Frameworks</label>
                                <div className="p-2.5 bg-black/40 border border-white/[0.04] rounded-lg text-slate-300 font-mono text-[11px]">
                                    {selectedRemediation.category || "Security Control"} • {selectedRemediation.gain} Posture Gain
                                </div>
                            </div>

                            <div>
                                <label className="text-[10px] font-mono uppercase text-slate-500 block mb-1">Impact & Implementation</label>
                                <p className="text-slate-300 leading-relaxed bg-black/30 p-3 rounded-lg border border-white/[0.04] text-[11.5px]">
                                    Enforcing this control immediately mitigates unauthorized lateral movement and satisfies audit criteria for SOC 2 Type 2 and ISO/IEC 27001.
                                </p>
                            </div>

                            <div className="p-3 bg-purple-950/20 border border-purple-500/30 rounded-xl flex items-center justify-between">
                                <span className="text-purple-300 font-mono font-bold">Earned Points:</span>
                                <span className="text-emerald-400 font-mono font-black text-[15px]">{selectedRemediation.gain}</span>
                            </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 pt-3 border-t border-white/[0.06]">
                            <button
                                type="button"
                                onClick={() => setSelectedRemediation(null)}
                                className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 rounded-lg font-mono text-[11px]"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                disabled={remediating}
                                onClick={() => handleApplyFixDirect(selectedRemediation)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md shadow-blue-600/20 font-mono text-[11px] disabled:opacity-50"
                            >
                                {remediating ? "Applying Fix..." : `Apply Fix (${selectedRemediation.gain})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── View All Findings Modal ─── */}
            {viewAllFindingsOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.1] rounded-2xl w-full max-w-3xl p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="flex items-center gap-2">
                                <Flame className="w-5 h-5 text-rose-500" />
                                <div>
                                    <h3 className="text-[15px] font-bold text-white">
                                        All Live Security Findings & Policy Events ({criticals.allFindings?.length || criticals.items?.length || 0})
                                    </h3>
                                    <span className="text-[10px] font-mono text-slate-400 block">
                                        Continuous sweep telemetry across active FortiGate gateways
                                    </span>
                                </div>
                            </div>
                            <button onClick={() => setViewAllFindingsOpen(false)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Filter Tabs & Search Bar */}
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-2.5">
                            <div className="flex items-center gap-1.5 p-1 bg-black/40 border border-white/[0.06] rounded-xl w-full sm:w-auto">
                                {[
                                    { id: "ALL", label: `All (${(criticals.allFindings || []).length || (criticals.items || []).length || 0})` },
                                    { id: "HIGH", label: `High Priority (${criticals.highPriority || 0})` },
                                    { id: "ALERT", label: `Criticals (${criticals.count || 0})` },
                                    { id: "WARNING", label: `Warnings` }
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setFindingsFilter(tab.id)}
                                        className={`px-3 py-1 rounded-lg text-[10.5px] font-mono font-bold transition-all ${
                                            findingsFilter === tab.id
                                                ? "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div className="relative w-full sm:w-56">
                                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2.5" />
                                <input
                                    type="text"
                                    value={findingsSearch}
                                    onChange={(e) => setFindingsSearch(e.target.value)}
                                    placeholder="Search gateway, IP, event..."
                                    className="w-full bg-black/40 border border-white/[0.06] focus:border-blue-500/50 rounded-xl pl-8 pr-3 py-1.5 text-[11px] text-white font-mono placeholder:text-slate-500 focus:outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* Filtered Findings List */}
                        <div className="max-h-96 overflow-y-auto space-y-2 pr-1">
                            {(() => {
                                const list = criticals.allFindings || criticals.items || [];
                                const filtered = list.filter(f => {
                                    const matchesFilter =
                                        findingsFilter === "ALL" ||
                                        (findingsFilter === "HIGH" && (f.severity === "HIGH" || f.severity === "CRITICAL" || f.severity === "ALERT")) ||
                                        (findingsFilter === "ALERT" && (f.severity === "ALERT" || f.severity === "CRITICAL")) ||
                                        (findingsFilter === "WARNING" && f.severity === "WARNING");
                                    const q = findingsSearch.toLowerCase();
                                    const matchesSearch = !q || f.title.toLowerCase().includes(q) || (f.service && f.service.toLowerCase().includes(q)) || (f.srcip && f.srcip.toLowerCase().includes(q));
                                    return matchesFilter && matchesSearch;
                                });

                                if (filtered.length === 0) {
                                    return (
                                        <div className="p-8 text-center text-slate-500 font-mono text-[11px]">
                                            No security findings matching "{findingsSearch || findingsFilter}"
                                        </div>
                                    );
                                }

                                return filtered.map((f, idx) => (
                                    <div
                                        key={f.id || idx}
                                        onClick={() => {
                                            setSelectedFinding(f);
                                            setViewAllFindingsOpen(false);
                                        }}
                                        className="p-3 bg-black/40 hover:bg-blue-950/20 border border-white/[0.04] hover:border-blue-500/40 rounded-xl flex items-center justify-between gap-3 transition-all cursor-pointer group"
                                    >
                                        <div className="min-w-0">
                                            <span className="text-white group-hover:text-blue-300 transition-colors font-medium block text-[12px] truncate">
                                                {f.title}
                                            </span>
                                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                                                <span className="text-[10px] font-mono text-slate-500">
                                                    {f.service || "FortiGate Gateway"} • {f.srcip !== "-" ? `Src: ${f.srcip}` : "Live Gateway Event"} • {f.time || "Recent"}
                                                </span>
                                                <span className="px-1.5 py-0.2 bg-purple-500/20 text-purple-300 border border-purple-500/30 rounded text-[8.5px] font-mono font-bold">
                                                    {f.primaryComplianceTag || "ISO A.8.20 • SOC 2 CC6.6"}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 flex-shrink-0">
                                            <span className={`px-2 py-0.5 rounded text-[9px] font-mono font-bold ${
                                                f.severity === "ALERT" || f.severity === "CRITICAL"
                                                    ? "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                                                    : f.severity === "HIGH"
                                                    ? "bg-amber-500/20 text-amber-400 border border-amber-500/30"
                                                    : "bg-blue-500/20 text-blue-400 border border-blue-500/30"
                                            }`}>
                                                {f.severity}
                                            </span>
                                            <span className="text-blue-400 group-hover:translate-x-0.5 transition-transform text-[11px] font-mono font-bold">
                                                Inspect →
                                            </span>
                                        </div>
                                    </div>
                                ));
                            })()}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-white/[0.06] text-[11px] font-mono text-slate-500">
                            <span>Click any finding to inspect network forensics & stage for firewall approval</span>
                            <button
                                onClick={() => setViewAllFindingsOpen(false)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold font-mono text-[11px] transition-all"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Audit Log Modal ─── */}
            {auditLogModal && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.1] rounded-2xl w-full max-w-lg p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px]">
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                            <div className="flex items-center gap-2">
                                <FileCheck className="w-4 h-4 text-emerald-400" />
                                <h3 className="text-[14px] font-bold text-white">
                                    Audit Telemetry: {auditLogModal.framework}
                                </h3>
                            </div>
                            <button onClick={() => setAuditLogModal(null)} className="text-slate-400 hover:text-white">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="bg-black/50 p-3.5 rounded-xl border border-white/[0.06] font-mono text-[10.5px] text-slate-300 space-y-2">
                            {auditLogModal.auditLogs ? (
                                auditLogModal.auditLogs.map((logLine, idx) => (
                                    <div key={idx} className={idx === 0 ? "text-emerald-400 font-bold" : idx === auditLogModal.auditLogs.length - 1 ? "text-blue-400 font-bold" : ""}>
                                        {logLine}
                                    </div>
                                ))
                            ) : (
                                <>
                                    <div className="text-emerald-400">✓ LIVE AUDIT TELEMETRY PIPELINE VERIFIED</div>
                                    <div>- Queried FortiGate Fleet: 19 active firewalls streaming live syslog</div>
                                    <div>- Queried FortiAnalyzer Central Data Lake: 565,000+ firewall logs indexed</div>
                                    <div>- Queried UTM & IPS Deep Inspection Engine: 0 perimeter breaches</div>
                                    <div className="text-blue-400">✓ Compliance score verified: {auditLogModal.score}</div>
                                </>
                            )}
                        </div>

                        <div className="flex items-center justify-end pt-3 border-t border-white/[0.06]">
                            <button
                                onClick={() => setAuditLogModal(null)}
                                className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold font-mono text-[11px]"
                            >
                                Close Audit Log
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Security & Compliance Controls Inspection Modal ─── */}
            {controlsModalOpen && (
                <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
                    <div className="bg-[#0F172A] border border-white/[0.1] rounded-2xl w-full max-w-4xl p-6 space-y-4 shadow-2xl animate-in zoom-in-95 duration-150 text-[12px] flex flex-col max-h-[90vh]">
                        
                        {/* Modal Header */}
                        <div className="flex items-center justify-between border-b border-white/[0.06] pb-3 shrink-0">
                            <div className="flex items-center gap-2.5">
                                <ShieldCheck className="w-5 h-5 text-emerald-400" />
                                <div>
                                    <h3 className="text-[15px] font-bold text-white tracking-tight flex items-center gap-2">
                                        Security & Compliance Controls
                                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-300 border border-blue-500/30 font-mono font-bold">
                                            38 Telemetry Controls
                                        </span>
                                    </h3>
                                    <span className="text-[10.5px] font-mono text-slate-400 block">
                                        Evaluated across ISO/IEC 27001:2022, SOC 2 Type 2, PCI-DSS & QueryTel Core Controls
                                    </span>
                                </div>
                            </div>
                            <button onClick={() => setControlsModalOpen(false)} className="text-slate-400 hover:text-white p-1 rounded-lg hover:bg-white/[0.04]">
                                <X className="w-4 h-4" />
                            </button>
                        </div>

                        {/* Filter Tabs & Search Bar */}
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 shrink-0">
                            <div className="flex items-center gap-1.5 p-1 bg-black/40 border border-white/[0.06] rounded-xl w-full sm:w-auto">
                                {[
                                    { id: "ALL", label: `All Controls (${(assessmentData?.controls?.items || []).length || 0})` },
                                    { id: "COMPLIANT", label: `✓ Compliant (${meta.compliant || 0})` },
                                    { id: "NON_COMPLIANT", label: `✗ Non-Compliant (${meta.notCompliant || 0})` }
                                ].map(tab => (
                                    <button
                                        key={tab.id}
                                        onClick={() => setControlsTab(tab.id)}
                                        className={`px-3 py-1.5 rounded-lg text-[11px] font-mono font-bold transition-all ${
                                            controlsTab === tab.id
                                                ? tab.id === "COMPLIANT" 
                                                    ? "bg-emerald-600 text-white shadow-md shadow-emerald-600/20"
                                                    : tab.id === "NON_COMPLIANT"
                                                    ? "bg-rose-600 text-white shadow-md shadow-rose-600/20"
                                                    : "bg-blue-600 text-white shadow-md shadow-blue-600/20"
                                                : "text-slate-400 hover:text-white hover:bg-white/[0.04]"
                                        }`}
                                    >
                                        {tab.label}
                                    </button>
                                ))}
                            </div>

                            <div className="relative w-full sm:w-64">
                                <Search className="w-3.5 h-3.5 text-slate-500 absolute left-2.5 top-2.5" />
                                <input
                                    type="text"
                                    value={controlsSearch}
                                    onChange={(e) => setControlsSearch(e.target.value)}
                                    placeholder="Search code, title, evidence..."
                                    className="w-full bg-black/40 border border-white/[0.06] focus:border-blue-500/50 rounded-xl pl-8 pr-3 py-1.5 text-[11px] text-white font-mono placeholder:text-slate-500 focus:outline-none transition-all"
                                />
                            </div>
                        </div>

                        {/* Controls List Container */}
                        <div className="overflow-y-auto space-y-2.5 pr-1 flex-1">
                            {(() => {
                                const allControls = assessmentData?.controls?.items || [];
                                const filtered = allControls.filter(ctrl => {
                                    const matchesTab = 
                                        controlsTab === "ALL" ? true :
                                        controlsTab === "COMPLIANT" ? ctrl.status === "COMPLIANT" :
                                        ctrl.status === "NON_COMPLIANT";
                                    const q = controlsSearch.toLowerCase();
                                    const matchesSearch = !q || 
                                        ctrl.title.toLowerCase().includes(q) || 
                                        ctrl.code.toLowerCase().includes(q) ||
                                        ctrl.framework.toLowerCase().includes(q) ||
                                        ctrl.category.toLowerCase().includes(q) ||
                                        (ctrl.evidence && ctrl.evidence.toLowerCase().includes(q));
                                    return matchesTab && matchesSearch;
                                });

                                if (filtered.length === 0) {
                                    return (
                                        <div className="p-12 text-center text-slate-500 font-mono text-[11px]">
                                            No security controls found matching "{controlsSearch || controlsTab}"
                                        </div>
                                    );
                                }

                                return filtered.map((ctrl) => {
                                    const isCompliant = ctrl.status === "COMPLIANT";
                                    return (
                                        <div
                                            key={ctrl.id}
                                            className={`p-3.5 rounded-xl border transition-all ${
                                                isCompliant
                                                    ? "bg-black/30 border-white/[0.04] hover:border-emerald-500/30"
                                                    : "bg-rose-950/10 border-rose-500/30 hover:border-rose-500/50 shadow-sm"
                                            }`}
                                        >
                                            <div className="flex flex-col md:flex-row md:items-start justify-between gap-3">
                                                <div className="space-y-1.5 flex-1 min-w-0">
                                                    <div className="flex items-center gap-2 flex-wrap">
                                                        <span className={`px-2 py-0.5 rounded text-[9.5px] font-mono font-black ${
                                                            isCompliant
                                                                ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                                                                : "bg-rose-500/20 text-rose-400 border border-rose-500/30"
                                                        }`}>
                                                            {isCompliant ? "✓ PASS • COMPLIANT" : "✗ FAIL • NON-COMPLIANT"}
                                                        </span>
                                                        <span className="px-2 py-0.5 rounded bg-purple-500/20 text-purple-300 border border-purple-500/30 text-[9px] font-mono font-bold">
                                                            {ctrl.code}
                                                        </span>
                                                        <span className="text-[10px] font-mono text-slate-400 font-medium">
                                                            {ctrl.framework} • {ctrl.category}
                                                        </span>
                                                        {ctrl.gateway && (
                                                            <span className="px-1.5 py-0.2 rounded bg-blue-500/10 text-blue-300 text-[8.5px] font-mono border border-blue-500/20 font-bold">
                                                                Gateway: {ctrl.gateway}
                                                            </span>
                                                        )}
                                                    </div>

                                                    <h4 className="text-[13px] font-bold text-white">
                                                        {ctrl.title}
                                                    </h4>

                                                    <div className="p-2.5 bg-black/40 rounded-lg border border-white/[0.03] text-[11px] font-mono text-slate-300 leading-relaxed">
                                                        <span className="text-slate-500 uppercase text-[9px] block font-bold mb-0.5">Live Telemetry Evidence:</span>
                                                        {ctrl.evidence}
                                                    </div>

                                                    {!isCompliant && ctrl.remediation && (
                                                        <div className="p-2.5 bg-amber-950/20 rounded-lg border border-amber-500/20 text-[11px] font-mono text-amber-200/90 leading-relaxed">
                                                            <span className="text-amber-400 uppercase text-[9px] block font-bold mb-0.5">Recommended Remediation:</span>
                                                            {ctrl.remediation}
                                                        </div>
                                                    )}
                                                </div>

                                                {/* Action Button for Non-Compliant */}
                                                {!isCompliant && (
                                                    <div className="flex md:flex-col items-center gap-2 flex-shrink-0 self-end md:self-center">
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setControlsModalOpen(false);
                                                                handleSubmitFindingToApprovals({
                                                                    id: ctrl.id,
                                                                    title: ctrl.title,
                                                                    srcip: ctrl.affectedIp || ctrl.ip || ctrl.srcip || "10.180.80.1",
                                                                    service: ctrl.gateway || "BCS-80-WAN-FW-01"
                                                                });
                                                            }}
                                                            className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg font-bold shadow-md shadow-blue-600/20 font-mono text-[10.5px] flex items-center gap-1 whitespace-nowrap transition-all"
                                                        >
                                                            <Shield className="w-3 h-3" />
                                                            Stage Firewall Fix →
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                });
                            })()}
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-white/[0.06] text-[11px] font-mono text-slate-500 shrink-0">
                            <span>Click "Stage Firewall Fix" on any non-compliant control to queue remediation into Approvals</span>
                            <button
                                onClick={() => setControlsModalOpen(false)}
                                className="px-4 py-2 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white rounded-lg font-bold font-mono text-[11px] transition-all"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
